import {
    Dispatch,
    Store,
    ThunkDispatch,
    UnknownAction,
} from "@reduxjs/toolkit";

import { IQueryParams } from "../../api/retail";
import { isoMiles } from "../../models/nominals";
import { preferredLocationSelector } from "../../store/common/selectors";
import { IRootState } from "../../store/reducers.interfaces";
import {
    registerCascade,
    registerLocationTriggeredCascade,
} from "../../utils/cascade";
import { metersToMiles } from "../../utils/maps";
import { storeFilterSelector } from "./selectors";
import { loadClosestStores } from "./thunks";

type IRootRetailState = Pick<IRootState, "retail" | "common">;

type RetailStoreWithThunkDispatch = Store<IRootRetailState> & {
    dispatch: ThunkDispatch<IRootRetailState, undefined, UnknownAction> &
        /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
        Dispatch<any>;
};

export const registerCascades = (
    store: RetailStoreWithThunkDispatch,
    extraStoreFilters?: Partial<IQueryParams>,
): void => {
    // Trigger store load when location becomes available (either from detection or rehydration)
    registerLocationTriggeredCascade(async () => {
        const state = store.getState();
        const location = preferredLocationSelector(state);
        const storeFilters = storeFilterSelector(state);

        if (location && storeFilters) {
            const key = `${JSON.stringify(location)} ${JSON.stringify(storeFilters)}`;

            // Convert from meters (internal representation) to miles for API
            const distanceMiles = isoMiles.unwrap(
                metersToMiles(storeFilters.distance),
            );
            const storeFiltersParams: Partial<IQueryParams> = {
                ...storeFilters,
                distance: distanceMiles,
            };

            await store.dispatch(
                loadClosestStores({
                    location,
                    extraFilters: {
                        ...extraStoreFilters,
                        ...storeFiltersParams,
                    },
                    loadKey: key,
                }),
            );
        }
    });

    // Load Stores when location changes
    registerCascade(store, {
        debounce: 500,

        transformState: (state) => {
            return state;
        },

        getKey: (state) => {
            // prefer entered location zip over detected location zip
            const location = preferredLocationSelector(state);
            // storeFilters for updating in every filter change
            const storeFilters = storeFilterSelector(state);
            return `${JSON.stringify(location)} ${JSON.stringify(
                storeFilters,
            )}`;
        },

        doCascade: async (key, state) => {
            if (key === state.retail.storesLoadedKey) {
                return true;
            }
            const location = preferredLocationSelector(state);
            if (!location) {
                return false;
            }
            // Prepare and spread current store filters
            const storeFilters = storeFilterSelector(state);
            // Convert from meters (internal representation) to miles for API
            const distanceMiles = isoMiles.unwrap(
                metersToMiles(storeFilters.distance),
            );
            const storeFiltersParams: Partial<IQueryParams> = {
                ...storeFilters,
                distance: distanceMiles,
            };
            // Use the loadClosestStores thunk to fetch and update stores
            // The thunk handles sorting and state updates in its fulfilled case
            await store.dispatch(
                loadClosestStores({
                    location,
                    extraFilters: {
                        ...extraStoreFilters,
                        ...storeFiltersParams,
                    },
                    loadKey: key,
                }),
            );
            return true;
        },
    });
};
